import { AttrKey, TelemetryComponent, TelemetryMode } from './contract';
import { resolveRoute } from './routeMap';

export interface TelemetryContext {
  component: TelemetryComponent;
  mode: TelemetryMode;
  page: string;
  storeNum: string;
  aisleCode: string;
  jobId: string;
  /** Internal only, not an attribute; W5 uses it to decide whether a missing jobId should hold. */
  carriesJobId: boolean;
}

// Every TelemetryContext key other than the envelope-only storeNum must be a valid ATTR key (D3).
// This must fail compilation (not just resolve to `never` unused) when that stops being true.
type _ContextKeysAreAttributes =
  Exclude<Exclude<keyof TelemetryContext, 'storeNum' | 'carriesJobId'>, AttrKey> extends never ? true : never;
type _AssertTrue<T extends true> = T;
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type _ContextCheck = _AssertTrue<[_ContextKeysAreAttributes] extends [never] ? false : true>;

export interface PegxDocumentContext {
  storeNum?: string;
  jobId?: string;
}

export interface ContextSources {
  pathname: string;
  search?: string;
  versionForReview?: string | null;
  solverJobId?: string | null;
  /** Store number the user selected earlier, used on routes without a store in the URL. */
  fallbackStoreNum?: string | null;
  /** PEGx only: values read from the opened PDF's metadata. */
  pegx?: PegxDocumentContext | null;
  /** The Visual Editor's edit-mode toggle; only affects the review/visual route. */
  editMode?: boolean;
}

const REVIEW_VISUAL = /^\/store\/[^/]+\/aisle\/[^/]+\/review\/visual$/;

export const firstJobIdFromSearch = (search?: string): string => {
  if (!search) {
    return '';
  }

  const value = new URLSearchParams(search).get('job_id') || '';

  // useVersionForReviewUrlSync writes job_id with arrayFormat 'comma'.
  return value.split(',')[0].trim();
};

/** Pure: router location + a few store fields -> the context of the common attribute block. */
export const deriveContext = ({
  pathname,
  search,
  versionForReview,
  solverJobId,
  fallbackStoreNum,
  pegx,
  editMode,
}: ContextSources): TelemetryContext => {
  const routeInfo = resolveRoute(pathname);
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  const isEditorReviewVisual = editMode === true && REVIEW_VISUAL.test(path);
  const base = {
    component: isEditorReviewVisual ? ('EDITOR' as const) : routeInfo.component,
    mode: isEditorReviewVisual ? ('EDIT' as const) : routeInfo.mode,
    page: pathname || '/',
    aisleCode: routeInfo.aisleCode,
    carriesJobId: routeInfo.carriesJobId,
  };

  // PEGx: storeNum and jobId come only from the PDF's metadata; no metadata means "" (contract §4.1).
  if (routeInfo.component === 'PEGX') {
    return { ...base, storeNum: pegx?.storeNum || '', jobId: pegx?.jobId || '' };
  }

  // URL first, then the store (ui-late-jobid-analysis.md, rule 2).
  const jobId = routeInfo.carriesJobId
    ? firstJobIdFromSearch(search) || versionForReview || solverJobId || ''
    : '';

  return { ...base, storeNum: routeInfo.storeNum || fallbackStoreNum || '', jobId };
};

/** A change of any of these mints a new viewId (contract §3.2). */
export const viewKey = ({ component, aisleCode, jobId, mode }: TelemetryContext): string =>
  [component, aisleCode, jobId, mode].join('|');
